import type { Webviewer } from "lib";

import type { OfflineDownloadStatusPayload, WebviewerEventName } from "types/events";

/**
 * The Webviewer events a native host can ask for over the command channel.
 *
 * Default-deny like the command allowlist, so an untrusted event name cannot become a listener on
 * the viewer's emitter.
 */
const SUBSCRIBABLE_EVENTS = [
    "selectionChanged",
    "offlineDownloadStatus",
    "viewerCloseRequested",
] as const satisfies readonly WebviewerEventName[];

type SubscribableEvent = (typeof SUBSCRIBABLE_EVENTS)[number];

/**
 * Holds the host's event subscriptions for as long as the viewer serves the command channel, and
 * forwards each subscribed event onto the channel as it is emitted.
 *
 * A repeated subscribe is a no-op rather than a second listener, so a host that lost track of what
 * it already asked for doesn't start receiving every event twice. Some events are only computed
 * while something listens for them, which is why subscriptions are dropped on dispose instead of
 * outliving the viewer that serves them. A new `offlineDownloadStatus` subscriber is sent the latest
 * download state at once, as the scene's state on open is usually told before the host subscribes.
 */
export class HostEventSubscriptions {
    private readonly unsubscribes = new Map<SubscribableEvent, () => void>();
    private latestDownloadState: OfflineDownloadStatusPayload | undefined;
    private readonly stopWatchingDownloadState: () => void;

    constructor(private readonly webviewer: Webviewer) {
        this.stopWatchingDownloadState = webviewer.events.addEventListener("offlineDownloadStatus", (payload) => {
            if (payload.status === "synchronized" || payload.status === "deleted") {
                this.latestDownloadState = payload;
            }
        });
    }

    subscribe(event: unknown) {
        const eventName = readEventName(event);
        if (this.unsubscribes.has(eventName)) {
            return;
        }

        this.unsubscribes.set(
            eventName,
            this.webviewer.events.addEventListener(eventName, (payload) => {
                this.webviewer.commandChannel.publishEvent(eventName, payload);
            }),
        );
        if (eventName === "offlineDownloadStatus" && this.latestDownloadState) {
            this.webviewer.commandChannel.publishEvent(eventName, this.latestDownloadState);
        }
    }

    unsubscribe(event: unknown) {
        const eventName = readEventName(event);
        this.unsubscribes.get(eventName)?.();
        this.unsubscribes.delete(eventName);
    }

    dispose() {
        this.stopWatchingDownloadState();
        for (const unsubscribe of this.unsubscribes.values()) {
            unsubscribe();
        }
        this.unsubscribes.clear();
    }
}

function readEventName(event: unknown): SubscribableEvent {
    if (!SUBSCRIBABLE_EVENTS.includes(event as SubscribableEvent)) {
        throw new Error(
            `Webviewer event "${String(event)}" is not exposed, expected one of ${SUBSCRIBABLE_EVENTS.join(", ")}`,
        );
    }

    return event as SubscribableEvent;
}
